/** IANA timezone helpers for scheduling and display. */

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function offsetMinutesForZone(timeZone, date = new Date()) {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const parts = Object.fromEntries(
      dtf.formatToParts(date).filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
    );
    const asUtc = Date.UTC(
      +parts.year,
      +parts.month - 1,
      +parts.day,
      +parts.hour,
      +parts.minute,
      +parts.second,
    );
    return Math.round((asUtc - date.getTime()) / 60000);
  } catch {
    return 0;
  }
}

function formatUtcOffset(minutes) {
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

function offsetLabel(timeZone, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    }).formatToParts(date);
    const name = parts.find((p) => p.type === "timeZoneName")?.value || "GMT";
    return name.replace(/^GMT/i, "UTC");
  } catch {
    return formatUtcOffset(offsetMinutesForZone(timeZone, date));
  }
}

let _cachedTimezoneOptions = null;

/** All IANA timezones sorted by UTC offset, labeled like (UTC+05:30) Asia/Kolkata */
export function getAllTimezoneOptions() {
  if (_cachedTimezoneOptions) return _cachedTimezoneOptions;

  let zones = [];
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = [
      "UTC",
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "Europe/London",
      "Europe/Paris",
      "Asia/Dubai",
      "Asia/Kolkata",
      "Asia/Singapore",
      "Asia/Tokyo",
      "Australia/Sydney",
    ];
  }

  const now = new Date();
  _cachedTimezoneOptions = zones
    .map((value) => {
      const offsetMinutes = offsetMinutesForZone(value, now);
      const off = offsetLabel(value, now);
      return {
        value,
        label: `(${off}) ${value.replace(/_/g, " ")}`,
        offsetMinutes,
      };
    })
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));

  return _cachedTimezoneOptions;
}

export function timezoneOptions(currentTz) {
  const all = getAllTimezoneOptions();
  const tz = currentTz || "UTC";
  if (all.some((t) => t.value === tz)) return all;
  const off = offsetLabel(tz);
  return [{ value: tz, label: `(${off}) ${tz.replace(/_/g, " ")}`, offsetMinutes: 0 }, ...all];
}

function tzOffsetMs(date, timeZone) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(date).filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    +parts.year,
    +parts.month - 1,
    +parts.day,
    +parts.hour,
    +parts.minute,
    +parts.second,
  );
  return asUtc - date.getTime();
}

export function isoToUserParts(iso, timeZone = "UTC") {
  if (!iso) return { date: "", time: "09:00" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "09:00" };

  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

export function userPartsToIso(dateStr, timeStr, timeZone = "UTC") {
  if (!dateStr) return "";
  const time = timeStr || "09:00";
  if (!timeZone || timeZone === "UTC") {
    return new Date(`${dateStr}T${time}:00.000Z`).toISOString();
  }
  const asIfUtc = new Date(`${dateStr}T${time}:00.000Z`);
  const offset = tzOffsetMs(asIfUtc, timeZone);
  return new Date(asIfUtc.getTime() - offset).toISOString();
}

export function formatInUserTimezone(iso, timeZone = "UTC", options = {}) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const off = offsetLabel(timeZone, d);
  const when = d.toLocaleString(undefined, {
    timeZone,
    dateStyle: "medium",
    timeStyle: "short",
    ...options,
  });
  return `${when} (${off})`;
}
